/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/pwa/registerServiceWorker.ts
 *
 * Rôle :
 *   Wrapper autour de `virtual:pwa-register` (vite-plugin-pwa). Demande à
 *   l'utilisateur de mettre à jour quand une nouvelle version est dispo,
 *   plutôt que d'imposer un refresh — cohérent avec les bonnes pratiques
 *   d'une PWA installée à l'écran d'accueil iOS / Android.
 *
 * Réutilisable :
 *   Logique 100 % générique. À copier tel quel dans toute autre PWA Vite.
 *
 * Voir : CLAUDE.md §8.2.3 (stratégies SW), §16.5 (config Workbox).
 *
 * @author Mika
 * @since 2026-05-04
 */

import { registerSW } from 'virtual:pwa-register';

/**
 * Enregistre le Service Worker, gère les notifications de mise à jour.
 *
 * @returns Une fonction qui force la mise à jour (à brancher sur un bouton
 *          UI plus tard, dans un écran « Réglages »).
 */
export function registerServiceWorker(): () => void {
  // `registerSW` est injecté par vite-plugin-pwa au build.
  // En dev (`devOptions.enabled: false` cf. vite.config.ts), il est no-op.
  const updateSW = registerSW({
    onNeedRefresh() {
      // Une nouvelle version est dispo — proposer à l'utilisateur d'updater.
      // Pour l'instant, on utilise un confirm() natif. À remplacer plus tard
      // par un toast TNE Vosges (cf. Stitch §15 prio 1 — micro-interactions).
      const accept = window.confirm(
        "Une nouvelle version de l'application est disponible.\n" +
        'Voulez-vous mettre à jour maintenant ?',
      );
      if (accept) {
        void updateSW(true);
      }
    },
    onOfflineReady() {
      // L'app est désormais utilisable hors-ligne. Pas d'alerte intrusive.
      console.info('[TNE 2026] Application disponible hors ligne.');
    },
    onRegisterError(error) {
      console.error('[TNE 2026] Erreur enregistrement Service Worker :', error);
    },
  });

  return () => void updateSW(true);
}
